import { useCallback, useRef, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { ApiError } from '@/api/client';
import { RequirePermission } from '@/components/RequirePermission';
import { statusLabel } from '@/components/StatusBadge';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime, formatNumber } from '@/i18n/format';
import type { EvidenceEntityKind, EvidenceKind, EvidenceRow } from '@/api/types';

const EVIDENCE_KINDS: readonly EvidenceKind[] = [
  'RECEIVING_DISCREPANCY',
  'DAMAGE',
  'UNKNOWN_ITEM',
  'RETURN_INSPECTION',
  'QUARANTINE',
  'WRITE_OFF',
];

const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp'];

function formatBytes(n: number, locale: 'en' | 'ru'): string {
  if (n < 1024) return `${formatNumber(n, locale)} B`;
  if (n < 1024 * 1024) return `${formatNumber(n / 1024, locale, { maximumFractionDigits: 1 })} KB`;
  return `${formatNumber(n / (1024 * 1024), locale, { maximumFractionDigits: 1 })} MB`;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // data:<mime>;base64,<payload> - only the payload is sent.
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * REGULATED EVIDENCE epic (ADR-074) UI wiring — the shared photo-evidence
 * gallery, reused by inbound/discrepancy, return, and order/operation detail
 * screens. Consumes the real `GET /api/v1/office/evidence` (list, metadata
 * only) and `GET /api/v1/office/evidence/{id}` (one record + its bytes,
 * base64) — never a placeholder. Read is gated by `EVIDENCE.READ` (the
 * fetch is skipped entirely if the caller lacks it, matching this app's UI-
 * hiding-only convention — the server remains the real boundary); upload is
 * gated by `EVIDENCE.WRITE` via `RequirePermission`, same pattern every
 * other write action in this app already uses.
 *
 * Bytes are fetched lazily per item on "View" rather than eagerly for the
 * whole list — a gallery can hold many photos and this app's API is
 * JSON+base64, not a CDN; eagerly pulling every photo's bytes on page load
 * would be needless bandwidth for evidence a manager may never open.
 */
export function EvidenceGallery({
  entityKind,
  entityId,
  ownerId,
  skuId = null,
  serialUnitId = null,
  canRead,
}: {
  readonly entityKind: EvidenceEntityKind;
  readonly entityId: string;
  readonly ownerId: string;
  readonly skuId?: string | null;
  readonly serialUnitId?: string | null;
  readonly canRead: boolean;
}): JSX.Element {
  const result = useApi(
    () => (canRead ? endpoints.evidenceList({ entityKind, entityId }) : Promise.resolve({ rows: [] as readonly EvidenceRow[] })),
    [entityKind, entityId, canRead],
  );
  const { t, locale } = useLanguage();

  const [expanded, setExpanded] = useState<Record<string, string>>({}); // evidenceId -> data URL
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [uploadKind, setUploadKind] = useState<EvidenceKind>('RECEIVING_DISCREPANCY');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onView = useCallback(
    async (row: EvidenceRow) => {
      if (expanded[row.id]) {
        setExpanded((prev) => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
        return;
      }
      setLoadingId(row.id);
      try {
        const full = await endpoints.evidenceGet(row.id);
        setExpanded((prev) => ({ ...prev, [row.id]: `data:${full.contentType};base64,${full.bytesBase64}` }));
      } catch {
        // leave collapsed - the row's own metadata stays visible
      } finally {
        setLoadingId(null);
      }
    },
    [expanded],
  );

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ACCEPTED_MIME.includes(file.type)) {
      setUploadError(t('evidence.onlyImagesAccepted'));
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const bytesBase64 = await readFileAsBase64(file);
      await endpoints.uploadEvidence({
        kind: uploadKind,
        contentType: file.type,
        bytesBase64,
        entityKind,
        entityId,
        ownerId,
        skuId,
        serialUnitId,
      });
      result.reload();
    } catch (err) {
      setUploadError(err instanceof ApiError ? `${err.code}` : t('evidence.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  if (!canRead) return <p className="sub">{t('evidence.noPermission')}</p>;
  if (result.status === 'loading') return <p className="sub">{t('evidence.loading')}</p>;
  if (result.status === 'error') return <p className="sub">{t('evidence.couldNotLoad')}</p>;

  const rows = result.data.rows;

  return (
    <div>
      {rows.length === 0 ? (
        <p className="sub">{t('evidence.noneUploaded')}</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
          {rows.map((row) => (
            <div key={row.id} className="card" style={{ padding: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{statusLabel(t, row.kind)}</div>
              <div className="sub" style={{ fontSize: 11 }}>
                {row.contentType} · {formatBytes(row.sizeBytes, locale)}
              </div>
              <div className="sub" style={{ fontSize: 11 }}>
                {formatDateTime(row.createdAt, locale)}
              </div>
              <button type="button" onClick={() => onView(row)} disabled={loadingId === row.id} style={{ marginTop: 8, width: '100%' }}>
                {loadingId === row.id ? t('common.loading') : expanded[row.id] ? t('common.hide') : t('evidence.view')}
              </button>
              {expanded[row.id] && (
                <img src={expanded[row.id]} alt={t('evidence.altText', { kind: statusLabel(t, row.kind) })} style={{ width: '100%', marginTop: 8, borderRadius: 4 }} />
              )}
            </div>
          ))}
        </div>
      )}

      <RequirePermission permission="EVIDENCE.WRITE">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={uploadKind} onChange={(e) => setUploadKind(e.target.value as EvidenceKind)} disabled={uploading}>
            {EVIDENCE_KINDS.map((k) => (
              <option key={k} value={k}>
                {statusLabel(t, k)}
              </option>
            ))}
          </select>
          <input ref={fileInputRef} type="file" accept={ACCEPTED_MIME.join(',')} onChange={onFileSelected} disabled={uploading} />
          {uploading && <span className="sub">{t('evidence.uploading')}</span>}
        </div>
        {uploadError && (
          <div className="sub" style={{ color: 'var(--danger)', marginTop: 4 }}>
            {uploadError}
          </div>
        )}
      </RequirePermission>
    </div>
  );
}
